import { useCallback, useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Headphones, Loader2, Phone } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Skel } from "@/components/app/Common";
import { api, get, errMsg } from "@/lib/api";
import { useLiveCall } from "@/lib/useLiveCall";
import { createMixedRecorder } from "@/lib/mixedRecorder";
import { uploadRecording } from "@/lib/videoUpload";
import { DeviceCheck } from "@/pages/interview/DeviceCheck";
import { CallStage } from "@/pages/interview/CallStage";

const WRAP_AT = 315;
const LAST_CALL = 45;

function Lobby({ st, onStart, busy }) {
  return (
    <section className="panel space-y-4 p-5 sm:p-8" data-testid="live-lobby">
      <h1 className="text-2xl font-semibold tracking-tight">{st.started ? "Rejoin your interview" : "Ready when you are"}</h1>
      <ul className="space-y-1.5 text-sm text-ink-secondary">
        <li className="flex gap-2"><Headphones size={16} className="mt-0.5 shrink-0 text-brand" /> Headphones and a quiet room work best.</li>
        <li>The AI interviewer will greet you and start asking questions. Answer out loud, as on a normal call.</li>
        <li>About 5 minutes; the call ends automatically at 10 minutes.{st.started ? ` About ${Math.ceil(st.remaining_seconds / 60)} min left.` : ""}</li>
      </ul>
      <Button onClick={onStart} disabled={busy} className="h-11 px-8 text-base" data-testid="live-start-btn">{busy ? <Loader2 size={18} className="animate-spin" /> : <Phone size={18} />} {st.started ? "Rejoin the call" : "Start the call"}</Button>
    </section>
  );
}

function Saving({ pct, failed, onRetry }) {
  return (
    <section className="panel space-y-3 p-6" data-testid="live-saving">
      <h1 className="text-xl font-semibold">{failed ? "We couldn't save your recording" : "Saving your interview…"}</h1>
      {failed ? <Button onClick={onRetry} data-testid="live-upload-retry">Try again</Button>
        : <><Progress value={pct} className="h-2 bg-brand-tint" data-testid="live-upload-progress" /><p className="text-sm text-ink-muted">{pct}% · please keep this page open</p></>}
    </section>
  );
}

export function LiveInterview({ token, onDone }) {
  const { data: st, refetch } = useQuery({ queryKey: ["iv-live", token], queryFn: () => get(`/public/interview/${token}/live/state`), refetchOnWindowFocus: false });
  const [cam, setCam] = useState(undefined);
  const [phase, setPhase] = useState("lobby");
  const [remaining, setRemaining] = useState(600);
  const [endAt, setEndAt] = useState(null);
  const [muted, setMuted] = useState(false);
  const [remote, setRemote] = useState(null);
  const [caption, setCaption] = useState("");
  const [save, setSave] = useState({ pct: 0, failed: false });
  const mic = useRef(null);
  const rec = useRef(null);
  const result = useRef(null);
  const pending = useRef([]);
  const flags = useRef({});
  const endRef = useRef(() => {});

  const flush = useCallback(async () => {
    if (!pending.current.length) return;
    const lines = pending.current.splice(0, 50);
    try { await api.post(`/public/interview/${token}/live/transcript`, { lines }); } catch { pending.current.unshift(...lines); }
  }, [token]);
  const onLine = useCallback((l) => {
    if (!l.text.trim()) return;
    pending.current.push({ ...l, at_ms: Date.now() });
    if (l.speaker === "ai") setCaption(l.text);
    flush();
  }, [flush]);
  const onRemote = useCallback((s) => { setRemote(s); rec.current?.setRemote(s); }, []);
  const call = useLiveCall(token, { onLine, onRemote, onEndRequested: () => endRef.current("ai") });

  useEffect(() => { if (st?.status === "completed" && phase === "lobby") onDone(); }, [st, phase, onDone]);
  useEffect(() => { if (st?.camera === false && cam === undefined) setCam(null); }, [st, cam]);
  useEffect(() => () => { mic.current?.getTracks().forEach((t) => t.stop()); }, []);

  const upload = useCallback(async () => {
    setSave({ pct: 0, failed: false });
    const ok = await uploadRecording(token, result.current, (pct) => setSave({ pct, failed: false }));
    if (ok) onDone(); else setSave({ pct: 0, failed: true });
  }, [token, onDone]);

  const end = useCallback(async (reason) => {
    if (flags.current.ended) return;
    flags.current.ended = true;
    call.close();
    setPhase("saving");
    await flush();
    try { await api.post(`/public/interview/${token}/live/end`, { reason }); } catch { /* server deadline finalises anyway */ }
    result.current = rec.current ? await rec.current.stop() : null;
    mic.current?.getTracks().forEach((t) => t.stop());
    if (result.current?.blob?.size) upload(); else onDone();
  }, [call, flush, token, upload, onDone]);
  endRef.current = end;

  const join = useCallback(async () => {
    const r = await call.connect(mic.current.getAudioTracks()[0], mic.current);
    if (r) { setRemaining(r.remaining); setEndAt(Date.now() + r.remaining * 1000); }
  }, [call]);

  const start = async () => {
    setPhase("connecting");
    try {
      mic.current = cam ? new MediaStream(cam.getAudioTracks()) : await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true } });
      rec.current = createMixedRecorder(cam, mic.current);
      await rec.current.resume();
    } catch { toast.error("We need your microphone for this interview."); setPhase("lobby"); return; }
    setPhase("call");
    if (st.started) { setRemaining(st.remaining_seconds); setEndAt(Date.now() + st.remaining_seconds * 1000); }
    await join();
  };

  useEffect(() => {
    if (phase !== "call" || !endAt) return undefined;
    const t = setInterval(() => setRemaining(Math.round((endAt - Date.now()) / 1000)), 500);
    return () => clearInterval(t);
  }, [phase, endAt]);
  const { system, status } = call;
  useEffect(() => {
    if (phase !== "call" || !endAt) return;
    if (remaining <= 0) { endRef.current("time_cap"); return; }
    if (status !== "live") return;
    if (remaining <= WRAP_AT && !flags.current.wrap) { flags.current.wrap = true; system("Time check: about five minutes have passed. Ask at most one more short question, then wrap up and call end_interview."); }
    if (remaining <= LAST_CALL && !flags.current.last) { flags.current.last = true; system("Time check: less than a minute is left. Thank the candidate, say goodbye now and call end_interview.", true); }
  }, [phase, endAt, remaining, status, system]);
  useEffect(() => { if (status === "timeup") endRef.current("time_cap"); }, [status]);
  useEffect(() => {
    if (phase !== "call") return undefined;
    const warn = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [phase]);

  const chooseDevice = async (stream) => {
    setCam(stream);
    try { await api.post(`/public/interview/${token}/voice/camera`, { camera: !!stream }); } catch { /* non-blocking */ }
  };
  const toggleMute = () => { const t = mic.current?.getAudioTracks()[0]; if (t) { t.enabled = muted; setMuted(!muted); } };
  const retry = async () => { try { await refetch(); await join(); } catch (e) { toast.error(errMsg(e)); } };

  if (!st) return <Skel className="h-80" />;
  if (cam === undefined) return <DeviceCheck onDone={chooseDevice} />;
  if (phase === "saving") return <Saving pct={save.pct} failed={save.failed} onRetry={upload} />;
  if (phase === "lobby" || phase === "connecting") return <Lobby st={st} onStart={start} busy={phase === "connecting"} />;
  return <CallStage call={call} cam={cam} remote={remote} remaining={remaining} muted={muted} onMute={toggleMute} onEnd={() => end("candidate")} onRetry={retry} caption={caption} />;
}
